import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { LogoBox, usePumpBrand } from "@/components/Logo";
import { fmtDate, fmtTime } from "@/utils/format";
import { useClosingPhoto } from "@/components/ClosingPhotoCapture";

import { useProducts } from "@/lib/products";

export default function PrintableReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const [shift, setShift] = useState(null);

  useEffect(() => { api.get(`/shifts/${id}`).then((r) => setShift(r.data)); }, [id]);
  if (!shift) return <div className="p-8">Loading…</div>;

  const isDual = !!shift.opening_mpd2;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between p-4">
          <Button variant="ghost" onClick={() => nav(-1)} data-testid="back-print">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="text-sm text-slate-500">
            {isDual ? "Two DSM Duty Slips will print — one per MPD." : "One DSM Duty Slip will print."}
          </div>
          <Button
            onClick={() => window.print()}
            data-testid="print-btn"
            className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
          >
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      <div className="print-root" data-testid="print-sheet">
        <Slip
          shift={shift}
          mpdLabel={isDual ? "MPD 1" : shift.mpd}
          opening={shift.opening}
          closing={shift.closing || {}}
          testing={shift.testing || {}}
          collection={shift.collection}
          totals={shift.totals_mpd1 || (isDual ? null : shift.totals)}
          nextEmpName={isDual ? shift.next_employee_mpd1_name : shift.next_employee_name}
        />
        {isDual && (
          <Slip
            shift={shift}
            mpdLabel="MPD 2"
            opening={shift.opening_mpd2}
            closing={shift.closing_mpd2 || {}}
            testing={shift.testing_mpd2 || {}}
            collection={shift.collection_mpd2}
            totals={shift.totals_mpd2}
            nextEmpName={shift.next_employee_mpd2_name}
            photoSide="2"
          />
        )}
      </div>

      <style>{`
        .print-root { max-width: 210mm; margin: 12px auto; }
        .slip { background: white; padding: 8mm 10mm; box-shadow: 0 1px 2px rgba(0,0,0,0.05); box-sizing: border-box; }
        .slip + .slip { margin-top: 12px; }
        .photo-box { height: 118mm; }

        @media print {
          .slip { display: flex; flex-direction: column; }
          .slip-bottom { flex: 1; min-height: 0; }
          .photo-box { height: auto; min-height: 90mm; max-height: 135mm; }
          @page { size: A4 portrait; margin: 6mm; }
          html, body { background: white !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-root { margin: 0; max-width: none; }
          .slip { box-shadow: none; padding: 4mm 6mm; height: 285mm; overflow: hidden; page-break-inside: avoid; }
          .slip + .slip { margin-top: 0; page-break-before: always; }
        }
      `}</style>
    </div>
  );
}

function Slip({ shift, mpdLabel, opening, closing, testing, collection, totals, nextEmpName, photoSide = "1" }) {
  const prices = shift.fuel_prices_used || {};
  const hasPhoto = !!(photoSide === "2" ? shift.closing_photo_mpd2 : shift.closing_photo);
  const photoUrl = useClosingPhoto(hasPhoto ? shift.id : null, photoSide);
  const brand = usePumpBrand();
  const products = useProducts(shift);

  const nozzleRow = (nz, fuel) => {
    const o = Number(opening?.[nz.k] || 0);
    const c = Number(closing?.[nz.k] || 0);
    const gross = Math.max(0, c - o);
    return { label: nz.l, fuel, o, c, gross };
  };

  const fuelBlocks = products.map((p) => {
    const rows = p.nozzles.map((nz, i) => nozzleRow({ k: nz, l: `${p.short} N${i + 1}` }, p.short));
    const gross = rows.reduce((s, r) => s + r.gross, 0);
    const test = Number(testing?.[p.key] || 0);
    const net = Math.max(0, gross - test);
    const rate = Number(prices?.[p.key] || 0);
    const amount = net * rate;
    return { fuel: `${p.short} (${p.unit})`, rows, gross, test, net, rate, amount };
  });

  const totalSale = totals?.grand_sales ?? fuelBlocks.reduce((s, b) => s + b.amount, 0);
  const paytm = Number(collection?.paytm || 0);
  const credit = Number(collection?.credit || 0);
  const card = Number(collection?.card || 0);
  const expectedCash = collection ? (collection.expected_cash ?? (totalSale - paytm - credit - card)) : null;
  const openingCash = Number(shift.opening_cash || 0);

  return (
    <section className="slip text-slate-900">
      {/* Header */}
      <header className="flex items-center gap-3 border-b-2 border-slate-900 pb-2">
        <LogoBox className="w-12 h-12" />
        <div className="flex-1">
          <div className="font-display text-[18px] leading-none font-bold uppercase">{brand.name}</div>
          <div className="text-[10px] text-slate-600 mt-0.5">{brand.sub.replace("Dam Duty · ", "")} Retail Outlet · DSM Duty Slip</div>
        </div>
        <div className="text-right">
          <div className="inline-block border-2 border-slate-900 rounded px-2 py-0.5 font-bold text-sm">{mpdLabel}</div>
          <div className="text-[9px] text-slate-600 mt-0.5">Shift #{shift.id.slice(0, 8).toUpperCase()}</div>
        </div>
      </header>

      {/* Meta grid */}
      <table className="w-full mt-2 text-[11px] border-collapse leading-tight">
        <tbody>
          <tr>
            <ThX>Date</ThX><TdX>{fmtDate(shift.business_date)}</TdX>
            <ThX>DSM Name</ThX><TdX>{shift.employee_name} <span className="text-slate-500">({shift.employee_code})</span></TdX>
            <ThX>Shift Start</ThX><TdX>{fmtTime(shift.start_time)}</TdX>
          </tr>
          <tr>
            <ThX>MPD</ThX><TdX>{mpdLabel}</TdX>
            <ThX>Shift No.</ThX><TdX>Shift {shift.shift_number}</TdX>
            <ThX>Shift End</ThX><TdX>{fmtTime(shift.end_time)}</TdX>
          </tr>
          <tr>
            <ThX>Opening Cash (₹)</ThX>
            <TdX>{openingCash ? openingCash.toFixed(2) : "__________"}</TdX>
            <ThX>{shift.previous_employee_name ? "From" : "Handover To"}</ThX>
            <TdX colSpan={3}>{shift.previous_employee_name || nextEmpName || "—"}</TdX>
          </tr>
        </tbody>
      </table>

      {/* Fuel Sales */}
      <div className="mt-2">
        <SectionTitle>Fuel Sales</SectionTitle>
        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr className="border-y border-slate-900 bg-slate-100">
              <th className="text-left py-0.5 px-1">Nozzle</th>
              <th className="text-left py-0.5 px-1">Fuel</th>
              <th className="text-right py-0.5 px-1">Opening</th>
              <th className="text-right py-0.5 px-1">Closing</th>
              <th className="text-right py-0.5 px-1">Testing</th>
              <th className="text-right py-0.5 px-1">Net Sale</th>
              <th className="text-right py-0.5 px-1">Rate (₹)</th>
              <th className="text-right py-0.5 px-1">Amount (₹)</th>
            </tr>
          </thead>
          <tbody className="font-mono-num">
            {fuelBlocks.map((b) => (
              <React.Fragment key={b.fuel}>
                {b.rows.map((r) => (
                  <tr key={r.label} className="border-b border-slate-300">
                    <td className="px-1 py-0.5 font-sans font-semibold">{r.label}</td>
                    <td className="px-1 py-0.5 font-sans">{r.fuel}</td>
                    <td className="text-right px-1 py-0.5">{r.o.toFixed(2)}</td>
                    <td className="text-right px-1 py-0.5">{r.c.toFixed(2)}</td>
                    <td></td>
                    <td className="text-right px-1 py-0.5">{r.gross.toFixed(2)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                ))}
                <tr className="border-b-2 border-slate-800 bg-slate-50 font-sans font-bold">
                  <td className="px-1 py-0.5" colSpan={2}>Total {b.fuel}</td>
                  <td></td><td></td>
                  <td className="text-right px-1 py-0.5 font-mono-num">{b.test.toFixed(2)}</td>
                  <td className="text-right px-1 py-0.5 font-mono-num">{b.net.toFixed(2)}</td>
                  <td className="text-right px-1 py-0.5 font-mono-num">{b.rate.toFixed(2)}</td>
                  <td className="text-right px-1 py-0.5 font-mono-num">{b.amount.toFixed(2)}</td>
                </tr>
              </React.Fragment>
            ))}
            <tr className="border-y-2 border-slate-900 bg-slate-100 font-bold">
              <td className="px-1 py-1" colSpan={7}>Total Sale</td>
              <td className="text-right px-1 py-1 font-mono-num">₹ {Number(totalSale).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Left: Collection + Cash Movement + signatures · Right: remarks + closing photo (extends upward) */}
      <div className="mt-2 grid grid-cols-2 gap-3 slip-bottom">
        <div className="flex flex-col">
          <SectionTitle>Collection Summary</SectionTitle>
          <table className="w-full text-[11px] border-collapse">
            <tbody>
              <RowKV k="Total Sale" v={`₹ ${Number(totalSale).toFixed(2)}`} bold />
              <RowKV k="By Paytm / UPI" v={collection ? `₹ ${paytm.toFixed(2)}` : ""} />
              <RowKV k="By Card" v={collection ? `₹ ${card.toFixed(2)}` : ""} />
              <RowKV k="By Credit / Udhar" v={collection ? `₹ ${credit.toFixed(2)}` : ""} />
              <RowKV k="Opening Cash" v={openingCash ? `₹ ${openingCash.toFixed(2)}` : ""} />
              <RowKV
                k="Expected Cash"
                v={expectedCash !== null ? `₹ ${expectedCash.toFixed(2)}` : ""}
                bold highlight
              />
              <RowKV k="Actual Cash (write manually)" v="" underline />
              <RowKV k="Shortage / Excess" v="" underline />
            </tbody>
          </table>

          <div className="mt-2"><SectionTitle>Cash Movement</SectionTitle></div>
          <table className="w-full text-[11px] border-collapse">
            <tbody>
              <RowKV k="Cash to Manager" v="" underline />
              <RowKV k="Cash to Safe" v="" underline />
              <RowKV k="Cash to Next DSM" v="" underline />
            </tbody>
          </table>

          <div className="flex-1 flex flex-col justify-end gap-8 text-[11px] pb-2 pt-6">
            <div>
              <div className="border-t-2 border-slate-900 pt-0.5 font-semibold">DSM Signature</div>
              <div className="text-slate-600 text-[10px]">{shift.employee_name}</div>
            </div>
            <div>
              <div className="border-t-2 border-slate-900 pt-0.5 font-semibold">Supervisor Signature</div>
              <div className="text-slate-600 text-[10px]">{brand.name}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="text-[10px] uppercase tracking-wide text-slate-600 font-bold">Testing Remarks</div>
          <div className="border border-slate-500 rounded px-1.5 py-1 min-h-[28px] text-[11px]">
            {testing?.remarks || ""}
          </div>
          <div className="mt-2"><SectionTitle>Closing Slip Photo</SectionTitle></div>
          <div className="border border-slate-500 rounded bg-slate-50 photo-box flex-1 grid place-items-center overflow-hidden" data-testid={`slip-photo-${photoSide}`}>
            {photoUrl ? (
              <img src={photoUrl} alt="Closing slip" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-slate-400">{hasPhoto ? "Loading photo…" : "No photo attached"}</span>
            )}
          </div>
        </div>
      </div>

      <footer className="mt-2 text-center text-[8px] text-slate-500 border-t border-slate-200 pt-1">
        Generated {new Date().toLocaleString("en-IN")} · Shift ID {shift.id}
      </footer>
    </section>
  );
}

// small react shim so we can use React.Fragment (already imported at top)

function SectionTitle({ children }) {
  return <div className="text-[10px] uppercase tracking-wide text-slate-600 font-bold mb-0.5">{children}</div>;
}

function ThX({ children }) {
  return (
    <td className="border border-slate-500 px-1.5 py-1 bg-slate-100 text-[9px] uppercase tracking-wide text-slate-700 font-semibold whitespace-nowrap w-[10%]">
      {children}
    </td>
  );
}
function TdX({ children, colSpan }) {
  return <td colSpan={colSpan} className="border border-slate-500 px-1.5 py-1 font-semibold">{children}</td>;
}
function RowKV({ k, v, bold, highlight, underline }) {
  return (
    <tr className="border-b border-slate-400">
      <td className={`px-1.5 py-1 ${bold ? "font-bold" : ""}`}>{k}</td>
      <td
        className={`text-right px-1.5 py-1 font-mono-num min-w-[80px] ${bold ? "font-bold" : ""} ${
          highlight ? "bg-slate-100" : ""
        } ${underline && !v ? "text-slate-300" : ""}`}
      >
        {v || (underline ? "__________" : "")}
      </td>
    </tr>
  );
}
